import * as React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Eye,
  GitBranch,
  Info,
  List,
  Shield,
  Zap,
} from 'lucide-react';
import { SegmentedControl, WorkspaceButton } from '@/components/ui/workspace';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useCanCurateFrameworks } from '@/lib/api/engagement-lifecycle';
import {
  useCoverageMatrixQuery,
  useCoverageQuery,
  type AreaCoverage,
  type AttentionItem,
  type CoverageMatrix as Matrix,
  type DiscoverySummary,
} from '@/lib/api/discovery';
import { cn } from '@/lib/utils/cn';
import {
  CoverageMeter,
  CoverageStatusChip,
  DocBadge,
  PersonAvatar,
  SourceStack,
} from '../coverage-bits';
import { useDiscoveryNavigate } from '../discovery-layout';
import {
  attentionCard,
  nextStepText,
  plural,
  type AttentionCardView,
  type DiscoveryDestination,
} from '../discovery-logic';

/**
 * The Discovery dashboard (T-0185, E12-F02 / E12-F06): once evidence has
 * been confirmed, Overview shows what needs attention, the KPIs, and
 * coverage by area as a list or as "who covered what". Prototype:
 * `coverageView`, `attentionRow` and `matrixView` in
 * `docs/discovery/prototype/discovery.js`.
 *
 * Every number is the API's: KPIs and cards from the summary, coverage
 * and its status words from `/coverage`, the matrix from
 * `/coverage/matrix`. An area opens in Findings (Decision 49).
 */

export function DiscoveryDashboard({
  engagementId,
  summary,
}: {
  engagementId: string;
  summary: DiscoverySummary;
}) {
  const go = useDiscoveryNavigate(engagementId);
  const canCurateFrameworks = useCanCurateFrameworks();
  const coverage = useCoverageQuery(engagementId);
  const matrix = useCoverageMatrixQuery(engagementId);
  const [view, setView] = React.useState<'list' | 'matrix'>('list');

  // Names for the sources a next step mentions (a new source is in the matrix).
  const names = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const s of matrix.data?.sources ?? []) m.set(s.key, s.name);
    for (const a of coverage.data?.areas ?? []) for (const s of a.source_keys) m.set(s.key, s.name);
    return m;
  }, [matrix.data, coverage.data]);

  const openArea = (areaId: string) => go({ to: 'findings', area: areaId });
  const active = view === 'list' ? coverage : matrix;
  const untagged = coverage.data?.untagged_confirmed ?? 0;

  return (
    <div className="px-6 pb-8 pt-4">
      <AttentionCards
        items={summary.attention}
        onGo={go}
        canCurateFrameworks={canCurateFrameworks}
      />
      <Kpis s={summary} />
      <div className="mb-2.5 flex flex-wrap items-center gap-3">
        <h2 className="text-h3 font-semibold text-ink-900">Coverage by area</h2>
        <span className="text-meta font-medium text-ink-500">
          A source is a person or a document. Two people from one transcript count once each; the
          same person twice counts once.
        </span>
        <span className="flex-1" />
        <SegmentedControl
          aria-label="Coverage view"
          value={view}
          onChange={setView}
          options={[
            { value: 'list', label: 'List' },
            { value: 'matrix', label: 'Who covered what' },
          ]}
        />
      </div>
      <div className="rounded-card border border-ink-100 bg-paper-0">
        {active.error ? (
          <div className="p-4">
            <ErrorMessage error={active.error} />
          </div>
        ) : view === 'list' && coverage.data ? (
          <CoverageTable areas={coverage.data.areas} names={names} onArea={openArea} />
        ) : view === 'matrix' && matrix.data ? (
          <CoverageMatrix
            matrix={matrix.data}
            labels={new Map((coverage.data?.areas ?? []).map((a) => [a.area_id, a.status_label]))}
            onArea={openArea}
          />
        ) : (
          <div className="flex items-center gap-2 p-4 text-body-sm text-ink-500">
            <Spinner /> Loading coverage…
          </div>
        )}
      </div>
      {untagged > 0 ? (
        <p className="mt-2 text-meta font-medium text-ink-500">
          {plural(untagged, 'confirmed item')} {untagged === 1 ? 'is' : 'are'} not tagged to an
          area, so {untagged === 1 ? 'it counts' : 'they count'} nowhere until tagged.{' '}
          <button
            type="button"
            className="text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
            onClick={() => go({ to: 'findings', area: 'all' })}
          >
            Open in Findings
          </button>
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attention cards (Decision 54.40: they stack and never replace each other)
// ---------------------------------------------------------------------------

const CARD_ICON: Record<string, typeof Zap> = {
  review_evidence: Zap,
  possible_duplicates: AlertTriangle,
  requirement_suggestions: List,
  process_maps: GitBranch,
  signoff: Shield,
};

const TONE: Record<AttentionCardView['tone'], { card: string; icon: string }> = {
  neutral: { card: 'border-l-ink-200', icon: 'text-ink-500' },
  ai: { card: 'border-l-ai-surface-border bg-ai-surface', icon: 'text-ai-ink' },
  warn: { card: 'border-l-posture-at-risk', icon: 'text-posture-at-risk' },
  ok: { card: 'border-l-posture-healthy', icon: 'text-posture-healthy' },
};

export function AttentionCards({
  items,
  onGo,
  canCurateFrameworks = false,
}: {
  items: ReadonlyArray<AttentionItem>;
  onGo: (d: DiscoveryDestination) => void;
  /** From `/me`: the reader may add processes in Firm settings (C2-74). */
  canCurateFrameworks?: boolean;
}) {
  return (
    <ul
      aria-label="Needs attention"
      className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4"
    >
      {items.map((item, i) => {
        const v = attentionCard(item, { canCurateFrameworks });
        const Icon =
          v.kind === 'review_evidence' && v.tone === 'ok' ? Check : (CARD_ICON[v.kind] ?? Info);
        return (
          <li
            key={`${v.kind}-${i}`}
            data-kind={v.kind}
            data-tone={v.tone}
            className={cn(
              'flex items-center gap-3 rounded-card border border-l-4 border-ink-100 bg-paper-0 px-3.5 py-3',
              TONE[v.tone].card,
            )}
          >
            <Icon className={cn('h-4 w-4 flex-none', TONE[v.tone].icon)} aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="text-body-sm font-semibold text-ink-900">{v.title}</div>
              {v.text ? <div className="text-meta font-medium text-ink-500">{v.text}</div> : null}
            </div>
            {v.action ? (
              <WorkspaceButton onClick={() => onGo(v.action!.destination)}>
                {v.action.label}
              </WorkspaceButton>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------

function Kpis({ s }: { s: DiscoverySummary }) {
  const k = s.kpis;
  const tiles = [
    {
      label: 'Covered',
      value: k.corroborated,
      of: k.areas,
      trend: s.corroboration_off
        ? 'one source each, corroboration off'
        : `${s.sources_required}+ independent sources`,
      source: 'From confirmed evidence',
    },
    {
      label: 'Need more sources',
      value: k.partial,
      trend: 'another person or document',
      source: 'From confirmed evidence',
    },
    {
      label: 'No evidence yet',
      value: k.none,
      trend: k.none ? 'nothing confirmed yet' : 'every area has evidence',
      source: 'From confirmed evidence',
    },
    {
      label: 'Awaiting review',
      value: k.awaiting_review,
      trend: 'AI proposals, not yet counted',
      source: 'From the review queue',
    },
    {
      label: 'Sessions held',
      value: k.interviews_held,
      of: k.interviews_total,
      trend: `${k.interviews_scheduled} scheduled`,
      source: 'From the session plan',
    },
  ];
  return (
    <div
      role="group"
      aria-label="Discovery KPIs"
      className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5"
    >
      {tiles.map((t) => (
        <div
          key={t.label}
          className="flex min-w-0 flex-col gap-1.5 rounded-card border border-ink-100 bg-paper-0 px-5 py-4"
        >
          <div className="text-meta font-medium text-ink-500">{t.label}</div>
          <div className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] text-ink-900 tabular-nums">
            {t.value.toLocaleString('en-US')}
            {t.of != null ? (
              <span className="text-[14px] font-medium text-ink-500"> of {t.of}</span>
            ) : null}
          </div>
          <div className="text-meta text-ink-500">{t.trend}</div>
          <KpiSourcePill source={t.source} className="mt-0.5 self-start" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coverage list
// ---------------------------------------------------------------------------

const TH =
  'sticky top-0 z-[1] border-b border-ink-100 bg-paper-100 px-3 py-2 text-left text-meta font-medium text-ink-500';
const TD = 'border-b border-ink-100 px-3 py-2.5 align-middle text-body-sm text-ink-700';

function CoverageTable({
  areas,
  names,
  onArea,
}: {
  areas: ReadonlyArray<AreaCoverage>;
  names: ReadonlyMap<string, string>;
  onArea: (areaId: string) => void;
}) {
  return (
    <table className="w-full table-fixed border-collapse">
      <colgroup>
        <col style={{ width: '30%' }} />
        <col style={{ width: 168 }} />
        <col style={{ width: 150 }} />
        <col style={{ width: 150 }} />
        <col style={{ width: 92 }} />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th className={TH}>Area</th>
          <th className={TH}>Status</th>
          <th className={TH}>Independent sources</th>
          <th className={TH}>Who and what</th>
          <th className={cn(TH, 'text-right')}>Evidence</th>
          <th className={TH}>Next step</th>
        </tr>
      </thead>
      <tbody>
        {areas.map((a) => {
          const next = nextStepText(a.next_step, names);
          return (
            <tr
              key={a.area_id}
              data-area={a.code}
              title="Open in Findings"
              className="cursor-pointer hover:bg-paper-50"
              onClick={() => onArea(a.area_id)}
            >
              <td className={TD}>
                <button
                  type="button"
                  className="text-left text-body-sm font-semibold text-ink-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArea(a.area_id);
                  }}
                >
                  {a.label}
                </button>
                {a.shown_on_screen ? (
                  <Eye
                    className="ml-1.5 inline h-3 w-3 text-posture-healthy"
                    aria-label="Seen on screen in a session"
                  />
                ) : null}
                <div className="truncate text-meta font-medium text-ink-500">{a.name}</div>
              </td>
              <td className={TD}>
                <CoverageStatusChip status={a.status} label={a.status_label} />
              </td>
              <td className={TD}>
                <CoverageMeter sources={a.sources} required={a.required} status={a.status} />
              </td>
              <td className={TD}>
                <SourceStack sources={a.source_keys} />
              </td>
              <td className={cn(TD, 'text-right tabular-nums')}>
                <span className="font-semibold text-ink-900">{a.confirmed_count}</span>
                {a.proposed_count ? (
                  <span className="ml-1 text-meta font-medium text-ai-ink" title="Awaiting review">
                    +{a.proposed_count}
                  </span>
                ) : null}
              </td>
              <td className={TD}>
                <span
                  className={cn(
                    'inline-flex items-start gap-1.5 text-meta',
                    next.done ? 'text-ink-500' : 'text-ink-700',
                  )}
                >
                  {next.done ? (
                    <Check
                      className="mt-0.5 h-3.5 w-3.5 flex-none text-posture-healthy"
                      aria-hidden="true"
                    />
                  ) : (
                    <ArrowRight
                      className="mt-0.5 h-3.5 w-3.5 flex-none text-accent-amber-600"
                      aria-hidden="true"
                    />
                  )}
                  <span>{next.text}</span>
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Who covered what
// ---------------------------------------------------------------------------

const STATUS_DOT = {
  corroborated: 'bg-posture-healthy',
  partial: 'bg-posture-at-risk',
  none: 'bg-ink-200',
} as const;
const STATUS_WORD = {
  corroborated: 'covered',
  partial: 'needs more sources',
  none: 'no evidence yet',
} as const;
const DOT =
  'inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-semibold leading-none';
const CONFIRMED = `${DOT} bg-ink-900 text-paper-0`;
const PROPOSED = `${DOT} border-[1.5px] border-dashed border-ai-ink bg-ai-surface text-ai-ink`;

function CoverageMatrix({
  matrix,
  labels,
  onArea,
}: {
  matrix: Matrix;
  labels: ReadonlyMap<string, string>;
  onArea: (areaId: string) => void;
}) {
  const cells = new Map(matrix.cells.map((c) => [`${c.area_id}|${c.key}`, c]));
  const short = (s: Matrix['sources'][number]) =>
    s.kind === 'document' ? (s.name.split(/[ .]/)[0] ?? s.name) : (s.name.split(' ')[0] ?? s.name);
  return (
    <div className="overflow-auto pb-3 pt-1">
      <table className="min-w-full border-separate border-spacing-0" aria-label="Who covered what">
        <thead>
          <tr>
            <th className="sticky left-0 z-[1] border-b border-ink-100 bg-paper-0 px-4 pb-2 text-left align-bottom text-meta font-medium text-ink-500">
              Area
            </th>
            {matrix.sources.map((s) => (
              <th
                key={s.key}
                title={s.name}
                className="min-w-[54px] border-b border-ink-100 px-1 pb-2 pt-2.5 text-center align-bottom font-medium"
              >
                <span className="inline-flex">
                  {s.kind === 'document' ? (
                    <DocBadge name={s.name} />
                  ) : (
                    <PersonAvatar id={s.id} name={s.name} size={24} />
                  )}
                </span>
                <div
                  aria-hidden="true"
                  className="mx-auto mt-1 max-w-[64px] truncate text-[11px] text-ink-500"
                >
                  {short(s)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.areas.map((a) => (
            <tr
              key={a.area_id}
              className="cursor-pointer hover:bg-paper-50"
              onClick={() => onArea(a.area_id)}
            >
              <td className="sticky left-0 whitespace-nowrap border-b border-ink-100 bg-paper-0 px-4 text-body-sm font-medium text-ink-900">
                <span
                  aria-hidden="true"
                  className={cn(
                    'mr-2 inline-block h-2 w-2 rounded-full align-middle',
                    STATUS_DOT[a.status],
                  )}
                />
                {a.label}
                <span className="sr-only">: {labels.get(a.area_id) ?? STATUS_WORD[a.status]}</span>
              </td>
              {matrix.sources.map((s) => {
                const c = cells.get(`${a.area_id}|${s.key}`);
                return (
                  <td key={s.key} className="h-[38px] border-b border-ink-100 text-center">
                    {c?.confirmed ? (
                      <span className={CONFIRMED} title={`${c.confirmed} confirmed`}>
                        {c.confirmed}
                      </span>
                    ) : c?.proposed ? (
                      <span className={PROPOSED} title={`${c.proposed} awaiting review`}>
                        {c.proposed}
                      </span>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2 px-4 pb-1 pt-3 text-meta text-ink-500">
        <span className={cn(CONFIRMED, 'h-[18px] w-[18px] text-[10px]')}>2</span> confirmed evidence
        <span className={cn(PROPOSED, 'ml-2 h-[18px] w-[18px] text-[10px]')}>1</span> awaiting
        review
        {(['corroborated', 'partial', 'none'] as const).map((st) => (
          <React.Fragment key={st}>
            <span
              aria-hidden="true"
              className={cn('ml-2 inline-block h-2 w-2 rounded-full', STATUS_DOT[st])}
            />
            {STATUS_WORD[st]}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
